import { useMemo, useState } from 'react';

import { AuditLocation, turbineSelectionKey } from '../types';
import styles from './styles.module.css';
import ChevronIcon from '@/assets/icons/chevron-accordion.svg?react';
import SettingsIcon from '@/assets/icons/settings.svg?react';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import Checkbox from '@/components/Form/Checkbox';
import SearchInput from '@/components/Form/searchInput';

interface SearchResult {
  key: string;
  locationId: string;
  turbineId: string;
  subType?: string;
}

interface FiltersSideMenuProps {
  locations: AuditLocation[];
  selectedKeys: Record<string, boolean>;
  allTurbinesSelected: boolean;
  showFuel: boolean;
  showEngineHours: boolean;
  showMonthlyStarts: boolean;
  showMonthlyStops: boolean;
  search: string;
  onToggleTurbine: (locationId: string, turbineId: string) => void;
  onToggleLocation: (locationId: string) => void;
  onToggleAllLocations: () => void;
  onToggleFuel: (checked: boolean) => void;
  onToggleEngineHours: (checked: boolean) => void;
  onToggleMonthlyStarts: (checked: boolean) => void;
  onToggleMonthlyStops: (checked: boolean) => void;
  onSearchChange: (value: string) => void;
  onAddTurbines: (keys: string[]) => void;
  onClearColumns: () => void;
  onClearTurbines: () => void;
}

export default function FiltersSideMenu({
  locations,
  selectedKeys,
  allTurbinesSelected,
  showFuel,
  showEngineHours,
  showMonthlyStarts,
  showMonthlyStops,
  search,
  onToggleTurbine,
  onToggleLocation,
  onToggleAllLocations,
  onToggleFuel,
  onToggleEngineHours,
  onToggleMonthlyStarts,
  onToggleMonthlyStops,
  onSearchChange,
  onAddTurbines,
  onClearColumns,
  onClearTurbines
}: FiltersSideMenuProps) {
  const [openLocations, setOpenLocations] = useState<Record<string, boolean>>(
    {}
  );
  const [resultChecks, setResultChecks] = useState<Record<string, boolean>>({});

  const normalizedSearch = search.trim().toLowerCase();

  const searchResults = useMemo<SearchResult[]>(() => {
    if (!normalizedSearch) return [];
    const results: SearchResult[] = [];
    locations.forEach(location => {
      location.turbines.forEach(turbine => {
        if (turbine.id.toLowerCase().includes(normalizedSearch)) {
          results.push({
            key: turbineSelectionKey(location.id, turbine.id),
            locationId: location.id,
            turbineId: turbine.id,
            subType: turbine.subType
          });
        }
      });
    });
    return results;
  }, [locations, normalizedSearch]);

  const isLocationFullySelected = (location: AuditLocation): boolean =>
    location.turbines.length > 0 &&
    location.turbines.every(
      turbine => selectedKeys[turbineSelectionKey(location.id, turbine.id)]
    );

  const toggleOpen = (locationId: string) => {
    setOpenLocations(prev => ({ ...prev, [locationId]: !prev[locationId] }));
  };

  const toggleResult = (key: string) => {
    setResultChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetSearchResults = () => {
    setResultChecks({});
    onSearchChange('');
  };

  const addAllResults = () => {
    if (searchResults.length === 0) return;
    onAddTurbines(searchResults.map(result => result.key));
    resetSearchResults();
  };

  const addSelectedResults = () => {
    const keys = searchResults
      .filter(result => resultChecks[result.key])
      .map(result => result.key);
    if (keys.length === 0) return;
    onAddTurbines(keys);
    resetSearchResults();
  };

  return (
    <aside id="auditFiltersSideMenu" className={styles.sideMenu}>
      <section className={styles.columnSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            <SettingsIcon width={16} height={16} />
            Filters
          </span>
          <button
            type="button"
            id="auditFiltersClearColumns"
            className={styles.clearButton}
            onClick={onClearColumns}
          >
            Clear
          </button>
        </div>
        <div className={styles.columnChecks}>
          <div className={styles.checkRowBold}>
            <Checkbox
              id="auditFilterFuelConsumption"
              label="Fuel Consumption"
              checked={showFuel}
              onChange={onToggleFuel}
              dimUnchecked
            />
          </div>
          <div className={styles.checkRowBold}>
            <Checkbox
              id="auditFilterFiredEngineHours"
              label="Engine Fired Hours"
              checked={showEngineHours}
              onChange={onToggleEngineHours}
              dimUnchecked
            />
          </div>
          <div className={styles.checkRowBold}>
            <Checkbox
              id="auditFilterMonthlyStarts"
              label="Monthly Starts"
              checked={showMonthlyStarts}
              onChange={onToggleMonthlyStarts}
              dimUnchecked
            />
          </div>
          <div className={styles.checkRowBold}>
            <Checkbox
              id="auditFilterMonthlyStops"
              label="Monthly Stops"
              checked={showMonthlyStops}
              onChange={onToggleMonthlyStops}
              dimUnchecked
            />
          </div>
        </div>
      </section>

      <section className={styles.turbineSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>
            <TurbineIcon width={16} height={16} />
            Turbines
          </span>
          <button
            type="button"
            id="auditFiltersClearTurbines"
            className={styles.clearButton}
            onClick={onClearTurbines}
          >
            Clear
          </button>
        </div>

        <div className={styles.searchWrapper}>
          <SearchInput
            variant="underline"
            placeholder="Search by turbine ID"
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            customClass={styles.search}
            showCross={Boolean(search)}
            resetSearch={resetSearchResults}
          />

          {normalizedSearch && (
            <div className={styles.searchResults}>
              {searchResults.length === 0 ? (
                <span className={styles.searchEmpty}>No matches found</span>
              ) : (
                <>
                  <div className={styles.searchResultsList}>
                    {searchResults.map(result => (
                      <div key={result.key} className={styles.searchResultRow}>
                        <Checkbox
                          id={`auditSearchResult-${result.key}`}
                          label={result.turbineId}
                          checked={Boolean(resultChecks[result.key])}
                          onChange={() => toggleResult(result.key)}
                          dimUnchecked
                        />
                        <span
                          className={`${styles.turbineModel} ${
                            resultChecks[result.key]
                              ? styles.turbineModelSelected
                              : ''
                          }`}
                        >
                          {result.subType}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.searchResultsActions}>
                    <button
                      type="button"
                      id="auditSearchAddAll"
                      className={styles.resultActionButton}
                      onClick={addAllResults}
                    >
                      Add all
                    </button>
                    <button
                      type="button"
                      id="auditSearchAddSelection"
                      className={styles.resultActionButton}
                      onClick={addSelectedResults}
                    >
                      Add selection
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className={styles.selectAllRow}>
          <Checkbox
            id="auditFilterSelectAllLocations"
            label="Select all locations"
            checked={allTurbinesSelected}
            onChange={onToggleAllLocations}
            dimUnchecked
          />
        </div>

        <div className={styles.locationList}>
          {locations.map(location => {
            const isOpen = Boolean(openLocations[location.id]);
            return (
              <div key={location.id} className={styles.locationGroup}>
                <div className={styles.locationHeader}>
                  <Checkbox
                    id={`auditFilterLocation-${location.id}`}
                    label={location.title}
                    checked={isLocationFullySelected(location)}
                    onChange={() => onToggleLocation(location.id)}
                    dimUnchecked
                  />
                  <button
                    type="button"
                    id={`auditFilterLocationToggle-${location.id}`}
                    className={styles.locationChevron}
                    aria-expanded={isOpen}
                    onClick={() => toggleOpen(location.id)}
                  >
                    <ChevronIcon
                      className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>

                {isOpen && (
                  <div className={styles.turbineList}>
                    {location.turbines.map(turbine => (
                      <div key={turbine.id} className={styles.turbineRow}>
                        <Checkbox
                          id={`auditFilterTurbine-${location.id}-${turbine.id}`}
                          label={turbine.id}
                          checked={Boolean(
                            selectedKeys[
                              turbineSelectionKey(location.id, turbine.id)
                            ]
                          )}
                          onChange={() =>
                            onToggleTurbine(location.id, turbine.id)
                          }
                          dimUnchecked
                        />
                        <span className={styles.turbineModel}>
                          {turbine.subType}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </aside>
  );
}
